import { useState, useEffect } from 'react'
import { useAssinatura } from '../hooks/useAssinatura'
import { useRecursoBloqueado } from '../hooks/useRecursoBloqueado'
import RecursoBloquadoModal from './RecursoBloquadoModal'

interface TrialProtectedRouteProps {
    children: React.ReactNode
    recursoNome: string
    recursoChave?: string
}

/**
 * FeatureGuard (antigo TrialProtectedRoute)
 * Gerencia o acesso a recursos baseado no plano do usuário e na configuração dinâmica do Admin.
 */
export default function TrialProtectedRoute({ children, recursoNome, recursoChave }: TrialProtectedRouteProps) {
    const { getAssinaturaStatus, assinatura } = useAssinatura()
    const assinaturaStatus = getAssinaturaStatus()
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Identificação do Recurso
    const chaveEfetiva = recursoChave || (
        recursoNome === 'Cursos' ? 'cursos' :
            recursoNome === 'Meu Driver' ? 'meu_driver' :
                recursoNome.toLowerCase().replace(/\s+/g, '_')
    )

    // 1. Obter Configuração de Permissões do Recurso
    const { config, loading: loadingConfig } = useRecursoBloqueado(chaveEfetiva)

    // 2. Identificar Plano Atual do Usuário
    const isTrial = assinaturaStatus.status === 'trial' && !assinaturaStatus.trial_expirado

    // Normalizando nome do plano (start, medium, pro)
    const nomePlano = assinatura?.plano?.nome?.toLowerCase() || ''

    // 3. Lógica de Bloqueio
    let acessoPermitido = true // Por padrão liberado, bloqueamos se encontrarmos motivo

    if (!loadingConfig && config) {
        if (isTrial) {
            // Se usuário é trial, checa acesso de trial
            // Suporte legado: se acesso_trial for nulo/undefined, usa o inverso de bloquear_no_trial
            if (config.acesso_trial !== undefined) {
                acessoPermitido = config.acesso_trial
            } else {
                acessoPermitido = !config.bloquear_no_trial
            }
        } else {
            // Usuário com assinatura ativa
            if (nomePlano.includes('start')) {
                acessoPermitido = config.acesso_start
            } else if (nomePlano.includes('medium') || nomePlano.includes('medio')) {
                acessoPermitido = config.acesso_medium
            } else if (nomePlano.includes('pro') || nomePlano.includes('avancado')) {
                acessoPermitido = config.acesso_pro
            }
            // Se plano não reconhecido, mantém liberado por segurança ou bloqueia se for strict.
            // Vou manter liberado para evitar bloqueio acidental de admins ou planos custom
        }
    }

    // Se plano não for identificado corretamente e não for trial, não bloqueia para evitar erro
    // Mas se for loading, espera

    // Efeito para abrir a modal
    useEffect(() => {
        if (!acessoPermitido && !loadingConfig && !isModalOpen) {
            setIsModalOpen(true)
        }
    }, [acessoPermitido, loadingConfig, isModalOpen])

    // Loading State Discreto
    if ((isTrial || assinatura) && loadingConfig) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        )
    }

    // Se bloqueado, mostra tela de bloqueio
    if (!acessoPermitido) {
        return (
            <>
                {/* Placeholder vazio */}
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
                    <div className="text-center">
                        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                            Recurso Não Incluído
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                            O recurso <strong>{recursoNome}</strong> não está disponível no seu plano atual ({isTrial ? 'Trial' : nomePlano || 'Básico'}).
                        </p>
                        <button
                            onClick={() => window.history.back()}
                            className="mt-6 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-200 transition-colors"
                        >
                            Voltar
                        </button>
                    </div>
                </div>

                {/* Modal de Bloqueio (Reaproveitando a existente, mas futuramente podemos ter uma específica para Upgrade) */}
                <RecursoBloquadoModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        window.history.back()
                    }}
                    recursoNome={recursoNome}
                    isTrial={isTrial}
                    planoAtual={nomePlano || 'Básico'}
                />
            </>
        )
    }

    // Se permitido, renderiza o recurso
    return <>{children}</>
}
